import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  GET_PRESET_EXPENSES,
  CREATE_PRESET_EXPENSE,
  UPDATE_PRESET_EXPENSE,
  DELETE_PRESET_EXPENSE,
  GET_HOURLY_EMPLOYEES,
  CREATE_HOURLY_EMPLOYEE,
  UPDATE_HOURLY_EMPLOYEE,
  DELETE_HOURLY_EMPLOYEE,
  GET_WORK_ORDER_BILLING_MARKUP,
  SET_WORK_ORDER_BILLING_MARKUP,
  GET_SERVICE_CHARGE_HOURLY,
  SET_SERVICE_CHARGE_HOURLY,
  GET_WORK_ORDER_SERVICE_CHARGE,
  SET_WORK_ORDER_SERVICE_CHARGE
} from '../graphql/presetExpensesAndHourlyEmployees';

const PresetExpenses = () => {
  const navigate = useNavigate();
  
  // Accordion state
  const [showPresetExpenses, setShowPresetExpenses] = useState(true);
  const [showHourlyEmployees, setShowHourlyEmployees] = useState(false);
  const [showBillingSettings, setShowBillingSettings] = useState(false);
  
  // Work Order Billing Markup State
  const [markupPercentage, setMarkupPercentage] = useState('');
  const [markupSaved, setMarkupSaved] = useState(false);
  
  // Service Charge Hourly Rate State
  const [serviceChargeHourly, setServiceChargeHourly] = useState('');
  const [serviceChargeSaved, setServiceChargeSaved] = useState(false);
  
  // Work Order Service Charge (flat fee) State
  const [workOrderServiceCharge, setWorkOrderServiceCharge] = useState('');
  const [serviceChargeFlatSaved, setServiceChargeFlatSaved] = useState(false);
  
  // Preset Expenses State
  const [newExpense, setNewExpense] = useState({ name: '', amount: '' });
  const [editingExpense, setEditingExpense] = useState(null);

  // Hourly Employees State
  const [newEmployee, setNewEmployee] = useState({ name: '', job: '', hourlyRate: '', isContractor: false });
  const [editingEmployee, setEditingEmployee] = useState(null);

  // ===== Work Order Billing Markup GraphQL =====
  const { data: markupData, loading: loadingMarkup } = useQuery(GET_WORK_ORDER_BILLING_MARKUP, {
    onCompleted: (data) => {
      if (data?.workOrderBillingMarkupPercentage !== undefined) {
        setMarkupPercentage(String(data.workOrderBillingMarkupPercentage));
      }
    }
  });

  const [setWorkOrderBillingMarkup, { loading: savingMarkup }] = useMutation(SET_WORK_ORDER_BILLING_MARKUP, {
    onCompleted: () => {
      toast.success('Billing markup percentage saved!');
      setMarkupSaved(true);
      setTimeout(() => setMarkupSaved(false), 2000);
    },
    onError: (error) => {
      console.error('Error saving markup percentage:', error);
      toast.error('Failed to save markup percentage. Please try again.');
    }
  });

  // Update local state when data loads
  useEffect(() => {
    if (markupData?.workOrderBillingMarkupPercentage !== undefined) {
      setMarkupPercentage(String(markupData.workOrderBillingMarkupPercentage));
    }
  }, [markupData]);

  const handleSaveMarkup = async () => {
    const percentage = parseFloat(markupPercentage) || 0;
    if (percentage < 0 || percentage > 1000) {
      toast.error('Markup percentage must be between 0 and 1000');
      return;
    }
    await setWorkOrderBillingMarkup({ variables: { percentage } });
  };

  // ===== Service Charge Hourly Rate GraphQL =====
  const { data: serviceChargeData, loading: loadingServiceCharge } = useQuery(GET_SERVICE_CHARGE_HOURLY, {
    onCompleted: (data) => {
      if (data?.serviceChargeHourlyRate !== undefined) {
        setServiceChargeHourly(String(data.serviceChargeHourlyRate));
      }
    }
  });

  const [setServiceChargeHourlyRate, { loading: savingServiceCharge }] = useMutation(SET_SERVICE_CHARGE_HOURLY, {
    onCompleted: () => {
      toast.success('Service charge hourly rate saved!');
      setServiceChargeSaved(true);
      setTimeout(() => setServiceChargeSaved(false), 2000);
    },
    onError: (error) => {
      console.error('Error saving service charge hourly rate:', error);
      toast.error('Failed to save service charge hourly rate. Please try again.');
    }
  });

  // Update service charge state when data loads
  useEffect(() => {
    if (serviceChargeData?.serviceChargeHourlyRate !== undefined) {
      setServiceChargeHourly(String(serviceChargeData.serviceChargeHourlyRate));
    }
  }, [serviceChargeData]);

  const handleSaveServiceCharge = async () => {
    const rate = parseFloat(serviceChargeHourly) || 0;
    if (rate < 0) {
      toast.error('Hourly rate must be a positive number');
      return;
    }
    await setServiceChargeHourlyRate({ variables: { rate } });
  };

  // ===== Work Order Service Charge (Flat Fee) GraphQL =====
  const { data: serviceChargeFlatData, loading: loadingServiceChargeFlat } = useQuery(GET_WORK_ORDER_SERVICE_CHARGE, {
    onCompleted: (data) => {
      if (data?.workOrderServiceCharge !== undefined) {
        setWorkOrderServiceCharge(String(data.workOrderServiceCharge));
      }
    }
  });

  const [setWorkOrderServiceChargeMutation, { loading: savingServiceChargeFlat }] = useMutation(SET_WORK_ORDER_SERVICE_CHARGE, {
    onCompleted: () => {
      toast.success('Service charge saved!');
      setServiceChargeFlatSaved(true);
      setTimeout(() => setServiceChargeFlatSaved(false), 2000);
    },
    onError: (error) => {
      console.error('Error saving service charge:', error);
      toast.error('Failed to save service charge. Please try again.');
    }
  });

  // Update service charge flat fee state when data loads
  useEffect(() => {
    if (serviceChargeFlatData?.workOrderServiceCharge !== undefined) {
      setWorkOrderServiceCharge(String(serviceChargeFlatData.workOrderServiceCharge));
    }
  }, [serviceChargeFlatData]);

  const handleSaveServiceChargeFlat = async () => {
    const amount = parseFloat(workOrderServiceCharge) || 0;
    if (amount < 0) {
      toast.error('Service charge must be a positive number');
      return;
    }
    await setWorkOrderServiceChargeMutation({ variables: { amount } });
  };

  // ===== Preset Expenses GraphQL =====
  const { data: presetExpensesData, loading: loadingExpenses } = useQuery(GET_PRESET_EXPENSES);
  const presetExpenses = presetExpensesData?.presetExpenses || [];

  const [createPresetExpenseMutation] = useMutation(CREATE_PRESET_EXPENSE, {
    refetchQueries: [{ query: GET_PRESET_EXPENSES }],
    onCompleted: () => {
      setNewExpense({ name: '', amount: '' });
    },
    onError: (error) => {
      console.error('Error creating preset expense:', error);
      alert('Failed to add preset expense. Please try again.');
    }
  });

  const [updatePresetExpenseMutation] = useMutation(UPDATE_PRESET_EXPENSE, {
    refetchQueries: [{ query: GET_PRESET_EXPENSES }],
    onCompleted: () => {
      setEditingExpense(null);
    },
    onError: (error) => {
      console.error('Error updating preset expense:', error);
      alert('Failed to update preset expense. Please try again.');
    }
  });

  const [deletePresetExpenseMutation] = useMutation(DELETE_PRESET_EXPENSE, {
    refetchQueries: [{ query: GET_PRESET_EXPENSES }],
    onError: (error) => {
      console.error('Error deleting preset expense:', error);
      alert('Failed to delete preset expense. Please try again.');
    }
  });

  // ===== Hourly Employees GraphQL =====
  const { data: hourlyEmployeesData, loading: loadingEmployees } = useQuery(GET_HOURLY_EMPLOYEES);
  const hourlyEmployees = hourlyEmployeesData?.hourlyEmployees || [];

  const [createHourlyEmployeeMutation] = useMutation(CREATE_HOURLY_EMPLOYEE, {
    refetchQueries: [{ query: GET_HOURLY_EMPLOYEES }],
    onCompleted: () => {
      setNewEmployee({ name: '', job: '', hourlyRate: '', isContractor: false });
    },
    onError: (error) => {
      console.error('Error creating hourly employee:', error);
      alert('Failed to add hourly employee. Please try again.');
    }
  });

  const [updateHourlyEmployeeMutation] = useMutation(UPDATE_HOURLY_EMPLOYEE, {
    refetchQueries: [{ query: GET_HOURLY_EMPLOYEES }],
    onCompleted: () => {
      setEditingEmployee(null);
    },
    onError: (error) => {
      console.error('Error updating hourly employee:', error);
      alert('Failed to update hourly employee. Please try again.');
    }
  });

  const [deleteHourlyEmployeeMutation] = useMutation(DELETE_HOURLY_EMPLOYEE, {
    refetchQueries: [{ query: GET_HOURLY_EMPLOYEES }],
    onError: (error) => {
      console.error('Error deleting hourly employee:', error);
      alert('Failed to delete hourly employee. Please try again.');
    }
  });

  // ===== Preset Expenses Functions =====
  const handleAddExpense = async () => {
    if (!newExpense.name.trim() || !newExpense.amount) {
      alert('Please fill in both name and amount');
      return;
    }

    await createPresetExpenseMutation({
      variables: {
        input: {
          name: newExpense.name.trim(),
          amount: parseFloat(newExpense.amount)
        }
      }
    });
  };

  const handleUpdateExpense = async () => {
    if (!editingExpense.name.trim() || !editingExpense.amount) {
      alert('Please fill in both name and amount');
      return;
    }

    await updatePresetExpenseMutation({
      variables: {
        id: editingExpense.id,
        input: {
          name: editingExpense.name.trim(),
          amount: parseFloat(editingExpense.amount)
        }
      }
    });
  };

  const handleDeleteExpense = async (id) => {
    if (window.confirm('Are you sure you want to delete this preset expense?')) {
      await deletePresetExpenseMutation({
        variables: { id }
      });
    }
  };

  // ===== Hourly Employees Functions =====
  const handleAddEmployee = async () => {
    if (!newEmployee.name.trim() || !newEmployee.job.trim() || !newEmployee.hourlyRate) {
      alert('Please fill in name, job, and hourly rate');
      return;
    }

    await createHourlyEmployeeMutation({
      variables: {
        input: {
          name: newEmployee.name.trim(),
          job: newEmployee.job.trim(),
          hourlyRate: parseFloat(newEmployee.hourlyRate),
          isContractor: newEmployee.isContractor
        }
      }
    });
  };

  const handleUpdateEmployee = async () => {
    if (!editingEmployee.name.trim() || !editingEmployee.job.trim() || !editingEmployee.hourlyRate) {
      alert('Please fill in name, job, and hourly rate');
      return;
    }

    await updateHourlyEmployeeMutation({
      variables: {
        id: editingEmployee.id,
        input: {
          name: editingEmployee.name.trim(),
          job: editingEmployee.job.trim(),
          hourlyRate: parseFloat(editingEmployee.hourlyRate),
          isContractor: editingEmployee.isContractor
        }
      }
    });
  };

  const handleDeleteEmployee = async (id) => {
    if (window.confirm('Are you sure you want to delete this hourly employee?')) {
      await deleteHourlyEmployeeMutation({
        variables: { id }
      });
    }
  };

  // Utility Functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(amount));
  };

  return (
    <div className="space-y-6">
      {/* Back to Reports Button */}
      <div className="flex items-center">
        <button
          onClick={() => navigate('/dashboard/reports')}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Reports
        </button>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Preset Expenses & Hourly Employees</h1>
        <p className="text-gray-600">Manage recurring expense types, hourly employees, and billing settings</p>
      </div>

      {/* Recurring Expense Types Section - Accordion */}
      <div className="bg-white rounded-lg shadow">
        <div 
          className="bg-sky-900 text-white p-6 cursor-pointer hover:bg-sky-800 transition-colors rounded-t-lg"
          onClick={() => setShowPresetExpenses(!showPresetExpenses)}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-medium">Recurring Expense Types</h2>
              <p className="text-sky-100 text-sm mt-1">
                Manage preset expense items that can be quickly added to reports
              </p>
            </div>
            <svg 
              className={`h-6 w-6 text-white transform transition-transform ${showPresetExpenses ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {showPresetExpenses && (
          <div className="p-6 space-y-6">
            {/* Add New Expense Form */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Preset Expense</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expense Name
                  </label>
                  <input
                    type="text"
                    value={newExpense.name}
                    onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    placeholder="e.g., Travel, Supplies, Equipment"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleAddExpense}
                    disabled={!newExpense.name.trim() || !newExpense.amount}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Preset
                  </button>
                </div>
              </div>
            </div>

            {/* Existing Preset Expenses */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Existing Preset Expenses ({presetExpenses.length})
              </h3>
              
              {loadingExpenses ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Loading preset expenses...</p>
                </div>
              ) : presetExpenses.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
                  </svg>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">No preset expenses</h3>
                  <p className="text-sm text-gray-500">Add your first preset expense using the form above.</p>
                </div>
              ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {presetExpenses.map((expense) => (
                      <li key={expense.id} className="px-6 py-4">
                        {editingExpense && editingExpense.id === expense.id ? (
                          /* Edit Mode */
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div>
                              <input
                                type="text"
                                value={editingExpense.name}
                                onChange={(e) => setEditingExpense({ ...editingExpense, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                              />
                            </div>
                            <div>
                              <div className="relative">
                                <span className="absolute left-3 top-2 text-gray-500">$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editingExpense.amount}
                                  onChange={(e) => setEditingExpense({ ...editingExpense, amount: e.target.value })}
                                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                />
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={handleUpdateExpense}
                                className="px-3 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingExpense(null)}
                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">{expense.name}</h4>
                              <p className="text-sm text-gray-500">{formatCurrency(expense.amount)}</p>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setEditingExpense(expense)}
                                className="text-sky-600 hover:text-sky-900 text-sm font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteExpense(expense.id)}
                                className="text-red-600 hover:text-red-900 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Hourly Employees Section - Accordion */}
      <div className="bg-white rounded-lg shadow">
        <div 
          className="bg-sky-900 text-white p-6 cursor-pointer hover:bg-sky-800 transition-colors rounded-t-lg"
          onClick={() => setShowHourlyEmployees(!showHourlyEmployees)}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-medium">Hourly Employees</h2>
              <p className="text-sky-100 text-sm mt-1">
                Manage hourly employees, their jobs, and hourly rates
              </p>
            </div>
            <svg 
              className={`h-6 w-6 text-white transform transition-transform ${showHourlyEmployees ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {showHourlyEmployees && (
          <div className="p-6 space-y-6">
            {/* Add New Employee Form */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Hourly Employee</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employee Name
                    </label>
                    <input
                      type="text"
                      value={newEmployee.name}
                      onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                      placeholder="e.g., John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job/Position
                    </label>
                    <input
                      type="text"
                      value={newEmployee.job}
                      onChange={(e) => setNewEmployee({ ...newEmployee, job: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                      placeholder="e.g., Laundry, Maintenance, Cleaning"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hourly Rate
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newEmployee.hourlyRate}
                        onChange={(e) => setNewEmployee({ ...newEmployee, hourlyRate: e.target.value })}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={handleAddEmployee}
                      disabled={!newEmployee.name.trim() || !newEmployee.job.trim() || !newEmployee.hourlyRate}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add Employee
                    </button>
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="newEmployeeContractor"
                    checked={newEmployee.isContractor}
                    onChange={(e) => setNewEmployee({ ...newEmployee, isContractor: e.target.checked })}
                    className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                  />
                  <label htmlFor="newEmployeeContractor" className="ml-2 block text-sm text-gray-700">
                    Contractor (Check this box if this person is a contractor)
                  </label>
                </div>
              </div>
            </div>

            {/* Existing Hourly Employees */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Hourly Employees ({hourlyEmployees.length})
              </h3>
              
              {loadingEmployees ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Loading hourly employees...</p>
                </div>
              ) : hourlyEmployees.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">No hourly employees</h3>
                  <p className="text-sm text-gray-500">Add your first hourly employee using the form above.</p>
                </div>
              ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {hourlyEmployees.map((employee) => (
                      <li key={employee.id} className="px-6 py-4">
                        {editingEmployee && editingEmployee.id === employee.id ? (
                          /* Edit Mode */
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                              <div>
                                <input
                                  type="text"
                                  value={editingEmployee.name}
                                  onChange={(e) => setEditingEmployee({ ...editingEmployee, name: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                  placeholder="Employee Name"
                                />
                              </div>
                              <div>
                                <input
                                  type="text"
                                  value={editingEmployee.job}
                                  onChange={(e) => setEditingEmployee({ ...editingEmployee, job: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                  placeholder="Job/Position"
                                />
                              </div>
                              <div>
                                <div className="relative">
                                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editingEmployee.hourlyRate}
                                  onChange={(e) => setEditingEmployee({ ...editingEmployee, hourlyRate: e.target.value })}
                                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                                />
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={handleUpdateEmployee}
                                  className="px-3 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => setEditingEmployee(null)}
                                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`editEmployeeContractor-${employee.id}`}
                                checked={editingEmployee.isContractor || false}
                                onChange={(e) => setEditingEmployee({ ...editingEmployee, isContractor: e.target.checked })}
                                className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`editEmployeeContractor-${employee.id}`} className="ml-2 block text-sm text-gray-700">
                                Contractor
                              </label>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-medium text-gray-900">{employee.name}</h4>
                                {employee.isContractor && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                    Contractor
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">{employee.job} • Hourly Rate: {formatCurrency(employee.hourlyRate)}</p>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setEditingEmployee(employee)}
                                className="text-sky-600 hover:text-sky-900 text-sm font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteEmployee(employee.id)}
                                className="text-red-600 hover:text-red-900 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Work Order Billing Settings Section - Accordion (at bottom) */}
      <div className="bg-white rounded-lg shadow">
        <div 
          className="bg-purple-900 text-white p-6 cursor-pointer hover:bg-purple-800 transition-colors rounded-t-lg"
          onClick={() => setShowBillingSettings(!showBillingSettings)}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-medium">Work Order Billing Settings</h2>
              <p className="text-purple-100 text-sm mt-1">
                Configure billing markup for work order expenses
              </p>
            </div>
            <svg 
              className={`h-6 w-6 text-white transform transition-transform ${showBillingSettings ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {showBillingSettings && (
          <div className="p-6">
            <div className="max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Billing Markup Percentage
              </label>
              <div className="flex items-center space-x-3">
                <div className="relative flex-1">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1000"
                    value={markupPercentage}
                    onChange={(e) => setMarkupPercentage(e.target.value)}
                    disabled={loadingMarkup}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="0"
                  />
                  <span className="absolute right-3 top-2 text-gray-500">%</span>
                </div>
                <button
                  onClick={handleSaveMarkup}
                  disabled={savingMarkup || loadingMarkup}
                  className={`px-4 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    markupSaved 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {savingMarkup ? 'Saving...' : markupSaved ? '✓ Saved' : 'Save'}
                </button>
              </div>
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-md">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-purple-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-purple-700">
                    <p className="font-medium">How it works:</p>
                    <p className="mt-1">
                      When editing work order expenses, if a base cost is entered but the billed amount is empty, 
                      the billed amount will automatically be calculated using this markup.
                    </p>
                    {markupPercentage && parseFloat(markupPercentage) > 0 && (
                      <p className="mt-2 font-medium">
                        Example: $100 base cost → ${(100 * (1 + parseFloat(markupPercentage) / 100)).toFixed(2)} billed amount
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Service Charge Hourly Rate */}
            <div className="max-w-md mt-8 pt-8 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Charge Hourly Rate
              </label>
              <div className="flex items-center space-x-3">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={serviceChargeHourly}
                    onChange={(e) => setServiceChargeHourly(e.target.value)}
                    disabled={loadingServiceCharge}
                    className="w-full px-3 py-2 pl-7 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-2 text-gray-500">/hr</span>
                </div>
                <button
                  onClick={handleSaveServiceCharge}
                  disabled={savingServiceCharge || loadingServiceCharge}
                  className={`px-4 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    serviceChargeSaved 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {savingServiceCharge ? 'Saving...' : serviceChargeSaved ? '✓ Saved' : 'Save'}
                </button>
              </div>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">How it works:</p>
                    <p className="mt-1">
                      This hourly rate is used to calculate labor charges for work orders based on the time logged.
                      The labor cost will appear in work order reports.
                    </p>
                    {serviceChargeHourly && parseFloat(serviceChargeHourly) > 0 && (
                      <p className="mt-2 font-medium">
                        Example: 2.5 hours logged → ${(2.5 * parseFloat(serviceChargeHourly)).toFixed(2)} labor charge
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Work Order Service Charge (Flat Fee) */}
            <div className="max-w-md mt-8 pt-8 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Work Order Service Charge
              </label>
              <div className="flex items-center space-x-3">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={workOrderServiceCharge}
                    onChange={(e) => setWorkOrderServiceCharge(e.target.value)}
                    disabled={loadingServiceChargeFlat}
                    className="w-full px-3 py-2 pl-7 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="0.00"
                  />
                </div>
                <button
                  onClick={handleSaveServiceChargeFlat}
                  disabled={savingServiceChargeFlat || loadingServiceChargeFlat}
                  className={`px-4 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    serviceChargeFlatSaved 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {savingServiceChargeFlat ? 'Saving...' : serviceChargeFlatSaved ? '✓ Saved' : 'Save'}
                </button>
              </div>
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-green-700">
                    <p className="font-medium">How it works:</p>
                    <p className="mt-1">
                      This is a flat service charge that is automatically added to each work order in reports.
                      It will appear as a separate line item in the work order billing.
                    </p>
                    {workOrderServiceCharge && parseFloat(workOrderServiceCharge) > 0 && (
                      <p className="mt-2 font-medium">
                        Each work order will include a ${parseFloat(workOrderServiceCharge).toFixed(2)} service charge
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PresetExpenses;
